import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

import {styled} from "@mui/material/styles";
import {CloseRounded} from "@mui/icons-material";

import {blue50, blue500, greyishBlue600} from "assets/jss/main";
import {getTranslation} from "domain/helpers/translations";

ListingActiveFiltersBar.propTypes = {
    filters: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.any.isRequired,
        label: PropTypes.node.isRequired,
        element: PropTypes.object,
    })).isRequired,
    onRemove: PropTypes.func.isRequired,
    onResetAll: PropTypes.func.isRequired,
};

const FILTER_TYPE_LABEL_IDS = {
    'Brand': 'retailer.product.list.filter.brand',
    'Category': 'retailer.product.list.filter.category',
    'Gender': 'retailer.product.list.filter.gender',
    'Status': 'retailer.product.list.filter.status',
    'Validated': 'retailer.product.list.filter.validated',
    'EshopId': 'retailer.product.list.filter.eshopid',
    'Lock': 'retailer.product.list.filter.lock',
    'Stock': 'retailer.product.list.filter.stocks',
    'LimitedDistribution': 'retailer.product.list.filter.limiteddistribution',
    'Channel': 'retailer.product.list.filter.channel',
    'PriceMin': 'retailer.product.list.filter.price.min',
    'PriceMax': 'retailer.product.list.filter.price.max',
    'DiscountMin': 'retailer.product.list.filter.discount.min',
    'DiscountMax': 'retailer.product.list.filter.discount.max',
    'SalesMin': 'retailer.product.list.filter.sales.min',
    'Benchmark': 'retailer.product.list.filter.benchmark',
    'Demand': 'retailer.product.list.filter.demand',
};

// returns the filter type prefix of a chip ("Statut : Actif", "Taille : M"), or null when unknown
export function getFilterTypeLabel(element, intl) {
    const type = element?.["@type"];

    // option value chips are prefixed with the option name itself (Taille, Couleur…)
    if (type === 'RetailerOptionValue') {
        return getTranslation(element.option ?? {translations: []})?.name ?? null;
    }

    const labelId = FILTER_TYPE_LABEL_IDS[type];

    return labelId ? intl.formatMessage({id: labelId, defaultMessage: type}) : null;
}

const Bar = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    width: '100%',
    fontSize: '14px',
    lineHeight: 'normal',
});

const ChipsContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px'
});

const BarLabel = styled('span')({
    color: greyishBlue600,
    fontWeight: 600,
    whiteSpace: 'nowrap'
});

const FilterChip = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    height: '32px',
    padding: '0 8px 0 12px',
    backgroundColor: blue50,
    borderRadius: '48px',
    color: blue500,
    whiteSpace: 'nowrap'
});

const RemoveIcon = styled(CloseRounded)({
    fontSize: '16px',
    cursor: 'pointer',
    flexShrink: 0
});

const ResetAll = styled('span')({
    color: blue500,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
});

function ListingActiveFiltersBar({filters, onRemove, onResetAll}) {
    return (
        <Bar>
            <ChipsContainer>
                <BarLabel>
                    <FormattedMessage id={"retailer.product.list.filter.active"}/>
                </BarLabel>
                {filters.map((filter) => (
                    <FilterChip key={filter.key}>
                        <span>{filter.label}</span>
                        <RemoveIcon onClick={() => onRemove(filter.element ?? filter.key)}/>
                    </FilterChip>
                ))}
            </ChipsContainer>
            <ResetAll onClick={onResetAll}>
                <FormattedMessage id={"retailer.product.list.filter.resetAll"}/>
            </ResetAll>
        </Bar>
    );
}

export default ListingActiveFiltersBar;
